import { defineComponent as _defineComponent } from 'vue';
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderSlot as _renderSlot, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, unref as _unref, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, createVNode as _createVNode } from "vue";
const _hoisted_1 = { class: "mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-10" };
const _hoisted_2 = { class: "space-y-12" };
const _hoisted_3 = { class: "grid items-stretch gap-10 lg:grid-cols-2" };
const _hoisted_4 = { class: "space-y-4" };
const _hoisted_5 = {
    key: 0,
    class: "text-sm uppercase tracking-[0.3em] text-neutral-500 dark:text-neutral-400"
};
const _hoisted_6 = { class: "text-3xl font-bold text-black dark:text-white lg:text-4xl" };
const _hoisted_7 = {
    key: 1,
    class: "text-lg text-neutral-600 dark:text-neutral-400"
};
const _hoisted_8 = {
    key: 0,
    class: "rounded-2xl border border-neutral-200 bg-white p-6 shadow-lg dark:border-neutral-800 dark:bg-neutral-900"
};
const _hoisted_9 = { class: "mb-4 flex items-center justify-between" };
const _hoisted_10 = {
    key: 0,
    class: "space-y-3"
};
const _hoisted_11 = {
    key: 1,
    class: "text-sm text-rose-600 dark:text-rose-400"
};
const _hoisted_12 = { class: "text-right text-neutral-800 dark:text-neutral-200" };
const _hoisted_13 = { class: "relative z-10 flex h-full flex-col gap-4 p-8 text-white" };
const _hoisted_14 = { class: "mt-2 break-words text-3xl font-bold" };
const _hoisted_15 = { class: "relative flex-1 min-h-[280px]" };
import { computed, ref, watch } from "vue";
import ProjectLocationMap from "./ProjectLocationMap.vue";
import { useOpeningHours } from "../composables/useOpeningHours";
export default /*@__PURE__*/ _defineComponent({
    ...{ name: "OpeningHoursSection" },
    __name: 'OpeningHours',
    props: {
        apiClient: {},
        heading: { default: "Unsere Öffnungszeiten" },
        description: { default: "" },
        accentText: { default: "Service" },
        artworkPosition: { default: "right" },
        deliveryArtworkPosition: { default: "right" },
        cacheKey: {},
        showDeliverySchedule: { type: Boolean, default: true },
        showOpeningHoursCard: { type: Boolean, default: true },
        showDeliveryCard: { type: Boolean, default: true },
        deliveryCardClass: { default: "bg-white dark:bg-neutral-900 rounded-2xl shadow-lg border border-neutral-200 dark:border-neutral-800 p-6" },
        deliveryTextClass: { default: "text-neutral-600 dark:text-neutral-400" },
        deliverySectionWrapperClass: { default: "bg-transparent" },
        placeIdOverride: { default: "" },
        markerColor: { default: "#000000" },
        mapType: { default: "roadmap" }
    },
    setup(__props) {
        const props = __props;
        const rootEl = ref(null);
        const cacheKey = computed(() => props.cacheKey ?? `opening-hours-${props.apiClient.getProjectIdentifier()}`);
        const { data, isLoading, error, refetch } = useOpeningHours({
            apiClient: props.apiClient,
            target: rootEl,
            cacheKey: cacheKey.value,
            once: true,
        });
        const statusDetails = computed(() => data.value?.status);
        const currentDayKey = computed(() => statusDetails.value?.currentDayKey);
        const openingSchedule = computed(() => data.value?.openingHours.schedule ?? []);
        const hasOpeningData = computed(() => openingSchedule.value.some((day) => day.enabled && day.hours.length > 0));
        const openingSoonLabel = computed(() => formatOpeningSoonLabel(statusDetails.value?.nextOpenTime));
        const isOpeningSoon = computed(() => Boolean(!statusDetails.value?.isOpen && openingSoonLabel.value));
        const statusLabel = computed(() => {
            if (isLoading.value)
                return "Status wird geladen …";
            if (statusDetails.value?.isOpen)
                return "Jetzt geöffnet";
            if (openingSoonLabel.value)
                return openingSoonLabel.value;
            return "Derzeit geschlossen";
        });
        const statusPillClass = computed(() => statusDetails.value?.isOpen
            ? "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-200 dark:border-emerald-800"
            : isOpeningSoon.value
                ? "bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-200 dark:border-amber-700"
                : "bg-rose-100 text-rose-800 border-rose-300 dark:bg-rose-900/30 dark:text-rose-200 dark:border-rose-800");
        const statusDotClass = computed(() => statusDetails.value?.isOpen ? "bg-emerald-500" : (isOpeningSoon.value ? "bg-amber-500" : "bg-rose-500"));
        const apiPlaceId = computed(() => data.value?.project?.placeId?.trim() ?? "");
        const projectPlaceId = computed(() => props.placeIdOverride?.trim() || apiPlaceId.value);
        const projectName = computed(() => data.value?.project?.name?.trim() ?? "");
        const placeDetailLabel = ref("");
        const placeDetailAddress = ref("");
        const placeDisplayLabel = computed(() => {
            if (placeDetailLabel.value)
                return placeDetailLabel.value;
            if (projectName.value)
                return projectName.value;
            return "Standort wird geladen …";
        });
        const mapLoaded = ref(false);
        const handlePlaceDetails = (details) => {
            console.log("[OpeningHours] handlePlaceDetails called with:", details);
            placeDetailLabel.value = details?.name?.trim() || "";
            placeDetailAddress.value = details?.formattedAddress?.trim() || "";
            // Hide placeholder when place details successfully resolved
            if (details?.name || details?.formattedAddress) {
                console.log("[OpeningHours] Place details received, setting mapLoaded = true");
                mapLoaded.value = true;
            }
            else {
                console.log("[OpeningHours] Place details empty, mapLoaded remains:", mapLoaded.value);
            }
        };
        const handleMapReady = () => {
            console.log("[OpeningHours] Map ready event received, setting mapLoaded = true");
            mapLoaded.value = true;
        };
        const handleMapError = (_message) => {
            console.warn("[OpeningHours] Map error received:", _message);
            // Don't reset mapLoaded if we already have place details
            // The map component will handle its own error UI (fallback iframe, etc)
            if (!mapLoaded.value) {
                console.log("[OpeningHours] No place details yet, keeping mapLoaded = false");
                mapLoaded.value = false;
            }
            else {
                console.log("[OpeningHours] Place details already loaded, ignoring map error for placeholder logic (mapLoaded stays true)");
            }
        };
        watch(projectPlaceId, (newVal, oldVal) => {
            console.log("[OpeningHours] projectPlaceId changed from", oldVal, "to", newVal, "- resetting mapLoaded");
            mapLoaded.value = false;
        });
        watch(mapLoaded, (newVal) => {
            console.log("[OpeningHours] mapLoaded changed to:", newVal);
        });
        const sectionsVisible = ref(false);
        watch([isLoading, error], ([loading, hasError]) => {
            sectionsVisible.value = !loading && !hasError;
        }, { immediate: true });
        const contentRevealClass = computed(() => sectionsVisible.value ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-4");
        const showOpeningCard = computed(() => props.showOpeningHoursCard);
        const openingArtworkOrderClass = computed(() => props.artworkPosition === "left" ? "order-2 lg:order-1" : "order-2 lg:order-2");
        const openingContentOrderClass = computed(() => props.artworkPosition === "left" ? "order-1 lg:order-2" : "order-1 lg:order-1");
        const formatDaySlots = (day) => {
            if (!day.enabled || !day.hours.length) {
                return "Geschlossen";
            }
            return day.hours.map((slot) => `${slot.open} – ${slot.close}`).join(", ");
        };
        const formatOpeningSoonLabel = (nextOpenTime) => {
            if (!nextOpenTime) {
                return "";
            }
            const hhmmMatch = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(nextOpenTime.trim());
            if (!hhmmMatch) {
                return "";
            }
            const [_, h, m] = hhmmMatch;
            const timezone = statusDetails.value?.timezone || data.value?.timezone || "Europe/Berlin";
            const nowParts = new Intl.DateTimeFormat("en-US", {
                timeZone: timezone,
                hour: "2-digit",
                minute: "2-digit",
                hour12: false
            }).formatToParts(new Date());
            const nowHour = Number(nowParts.find((part) => part.type === "hour")?.value || "0");
            const nowMinute = Number(nowParts.find((part) => part.type === "minute")?.value || "0");
            const nowMinutes = (nowHour * 60) + nowMinute;
            const nextMinutes = (Number(h) * 60) + Number(m);
            const diffMinutes = nextMinutes - nowMinutes;
            if (diffMinutes >= 0 && diffMinutes <= 180) {
                return `Öffnet bald ${h}:${m} Uhr`;
            }
            return "";
        };
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock("section", {
                ref_key: "rootEl",
                ref: rootEl,
                class: "relative bg-neutral-50 transition-colors duration-300 dark:bg-neutral-950 mb-10"
            }, [
                _createElementVNode("div", _hoisted_1, [
                    _createElementVNode("div", _hoisted_2, [
                        _createElementVNode("section", _hoisted_3, [
                            _createElementVNode("div", {
                                class: _normalizeClass(['space-y-8 py-4', openingContentOrderClass.value])
                            }, [
                                _createElementVNode("div", _hoisted_4, [
                                    (_ctx.accentText)
                                        ? (_openBlock(), _createElementBlock("p", _hoisted_5, _toDisplayString(_ctx.accentText), 1 /* TEXT */))
                                        : _createCommentVNode("v-if", true),
                                    _createElementVNode("h2", _hoisted_6, [
                                        _renderSlot(_ctx.$slots, "title", {}, () => [
                                            _createTextVNode(_toDisplayString(_ctx.heading), 1 /* TEXT */)
                                        ])
                                    ]),
                                    (_ctx.description)
                                        ? (_openBlock(), _createElementBlock("p", _hoisted_7, [
                                            _renderSlot(_ctx.$slots, "description", {}, () => [
                                                _createTextVNode(_toDisplayString(_ctx.description), 1 /* TEXT */)
                                            ])
                                        ]))
                                        : _createCommentVNode("v-if", true)
                                ]),
                                (showOpeningCard.value)
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                                        _createElementVNode("div", _hoisted_9, [
                                            _cache[1] || (_cache[1] = _createElementVNode("div", null, [
                                                _createElementVNode("h3", { class: "text-xl font-semibold text-black dark:text-white" }, "Öffnungszeiten")
                                            ], -1 /* CACHED */)),
                                            (!_unref(isLoading) && _unref(error))
                                                ? (_openBlock(), _createElementBlock("button", {
                                                    key: 0,
                                                    type: "button",
                                                    class: "text-sm font-semibold text-black hover:underline dark:text-white",
                                                    onClick: _cache[0] || (_cache[0] =
                                                        //@ts-ignore
                                                        (...args) => (_unref(refetch) && _unref(refetch)(...args)))
                                                }, " Erneut laden "))
                                                : _createCommentVNode("v-if", true)
                                        ]),
                                        (_unref(isLoading))
                                            ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                                                (_openBlock(), _createElementBlock(_Fragment, null, _renderList(5, (n) => {
                                                    return _createElementVNode("div", {
                                                        key: n,
                                                        class: "flex items-center justify-between animate-pulse"
                                                    }, [...(_cache[2] || (_cache[2] = [
                                                            _createElementVNode("div", { class: "h-4 w-24 rounded bg-neutral-200 dark:bg-neutral-800" }, null, -1 /* CACHED */),
                                                            _createElementVNode("div", { class: "h-4 w-32 rounded bg-neutral-200 dark:bg-neutral-800" }, null, -1 /* CACHED */)
                                                        ]))]);
                                                }), 64 /* STABLE_FRAGMENT */))
                                            ]))
                                            : (_unref(error))
                                                ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                                                    _renderSlot(_ctx.$slots, "error", { error: _unref(error) }, () => [
                                                        _createTextVNode(_toDisplayString(_unref(error)), 1 /* TEXT */)
                                                    ])
                                                ]))
                                                : (hasOpeningData.value)
                                                    ? (_openBlock(), _createElementBlock("ul", {
                                                        key: 2,
                                                        class: _normalizeClass(["space-y-3", ['transition duration-500 ease-out', contentRevealClass.value]])
                                                    }, [
                                                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(openingSchedule.value, (day) => {
                                                            return (_openBlock(), _createElementBlock("li", {
                                                                key: day.key,
                                                                class: "flex items-start justify-between gap-3 text-sm"
                                                            }, [
                                                                _createElementVNode("span", {
                                                                    class: _normalizeClass([
                                                                        'font-medium',
                                                                        day.key === currentDayKey.value
                                                                            ? 'text-black dark:text-white'
                                                                            : 'text-neutral-500 dark:text-neutral-400',
                                                                    ])
                                                                }, _toDisplayString(day.label), 3 /* TEXT, CLASS */),
                                                                _createElementVNode("span", _hoisted_12, _toDisplayString(formatDaySlots(day)), 1 /* TEXT */)
                                                            ]));
                                                        }), 128 /* KEYED_FRAGMENT */))
                                                    ], 2 /* CLASS */))
                                                    : (_openBlock(), _createElementBlock("p", {
                                                        key: 3,
                                                        class: _normalizeClass(["text-sm text-neutral-500 dark:text-neutral-400", ['transition duration-500 ease-out', contentRevealClass.value]])
                                                    }, " Keine Öffnungszeiten hinterlegt. ", 2 /* CLASS */))
                                    ]))
                                    : _createCommentVNode("v-if", true)
                            ], 2 /* CLASS */),
                            _createElementVNode("div", {
                                class: _normalizeClass(['relative min-h-[320px] overflow-hidden rounded-3xl', openingArtworkOrderClass.value])
                            }, [
                                _renderSlot(_ctx.$slots, "opening-artwork", {
                                    status: statusDetails.value,
                                    isOpen: statusDetails.value?.isOpen
                                }, () => [
                                    _cache[4] || (_cache[4] = _createElementVNode("div", { class: "absolute inset-0 bg-gradient-to-br from-black via-grey-700 to-black opacity-90" }, null, -1 /* CACHED */)),
                                    _cache[5] || (_cache[5] = _createElementVNode("div", { class: "absolute inset-0 backdrop-blur-[2px]" }, null, -1 /* CACHED */)),
                                    _createElementVNode("div", _hoisted_13, [
                                        _createElementVNode("div", null, [
                                            _cache[3] || (_cache[3] = _createElementVNode("p", { class: "text-sm uppercase tracking-[0.4em] opacity-80" }, "Unser Standort", -1 /* CACHED */)),
                                            _createElementVNode("p", _hoisted_14, _toDisplayString(placeDisplayLabel.value), 1 /* TEXT */),
                                            _createElementVNode("span", {
                                                class: _normalizeClass(["inline-flex items-center rounded-full border px-3 py-1 text-sm font-semibold mt-2", statusPillClass.value])
                                            }, [
                                                _createElementVNode("span", {
                                                    class: _normalizeClass(["mr-2 h-2 w-2 rounded-full", statusDotClass.value])
                                                }, null, 2 /* CLASS */),
                                                _createTextVNode(" " + _toDisplayString(statusLabel.value), 1 /* TEXT */)
                                            ], 2 /* CLASS */)
                                        ]),
                                        _createElementVNode("div", _hoisted_15, [
                                            _createVNode(ProjectLocationMap, {
                                                "place-id": projectPlaceId.value,
                                                class: "absolute inset-0 rounded-2xl border border-white/40 bg-white/10 shadow-2xl backdrop-blur overflow-hidden",
                                                "height-class": "h-full",
                                                "status-with-credentials": false,
                                                "use-iframe-fallback": true,
                                                "marker-color": props.markerColor,
                                                "map-type": props.mapType,
                                                onPlaceDetails: handlePlaceDetails,
                                                onMapReady: handleMapReady,
                                                onMapError: handleMapError
                                            }, null, 8 /* PROPS */, ["place-id", "marker-color", "map-type"])
                                        ])
                                    ])
                                ])
                            ], 2 /* CLASS */)
                        ])
                    ])
                ])
            ], 512 /* NEED_PATCH */));
        };
    }
});
